import { fetchRest } from '@/lib/hook/tanstackQuery'
import { ApiMutationEndpoint } from '@/src/ApiMutationEndpoint'

export interface ProductAddBasketResponse {
  product_id: string | number
  product?: string
  available: boolean
  available_amount: number | null
}

interface ProductInput {
  product_id: string | number
  product?: string
  amount: number | string
  current_cart_amount?: number
  [k: string]: any
}

/**
 * Replace addProductsBasket Apollo resolver. Logique:
 *  1. GET products/:id pour chaque produit -> check stock + tracking
 *  2. POST cart_content {products: filtered avail} si au moins 1 dispo
 *  3. Retourne ProductAddBasketResponse[] avec available/available_amount
 */
export const addProductsBasket = async (
  products: ProductInput[]
): Promise<ProductAddBasketResponse[]> => {
  const productsByPid: Record<string, ProductInput> = {}
  products.forEach(p => {
    productsByPid[String(p.product_id)] = p
  })

  // 1. Stock check parallel
  const checks: ProductAddBasketResponse[] = await Promise.all(
    Object.values(productsByPid).map(async product => {
      try {
        const productInfo: any = await fetchRest({
          endpoint: `products/${product.product_id}`,
          method: 'GET',
        })

        const stockAmount = Number(productInfo?.amount ?? 0)
        const hasInfiniteStock = productInfo?.tracking == 'D'
        const currentCartAmount = Number(product.current_cart_amount ?? 0)
        const remainingStock = stockAmount - currentCartAmount

        return {
          product_id: productInfo?.product_id ?? product.product_id,
          product: productInfo?.product ?? product.product,
          available: hasInfiniteStock || remainingStock >= Number(product.amount),
          available_amount: hasInfiniteStock ? null : remainingStock,
        }
      } catch {
        return {
          product_id: product.product_id,
          product: product.product,
          available: false,
          available_amount: null,
        }
      }
    })
  )

  // 2. Filter avail + POST
  const finalProducts: Record<string, ProductInput> = {}
  checks.forEach(r => {
    if (r.available) finalProducts[String(r.product_id)] = productsByPid[String(r.product_id)]
  })

  if (Object.keys(finalProducts).length > 0) {
    await fetchRest({
      endpoint: ApiMutationEndpoint.BASKET.ADD_PRODUCT.endpoint,
      method: 'POST',
      body: { products: finalProducts },
    })
  }

  return checks
}
